"use client";

import { useState, useCallback, useRef, useEffect } from 'react'

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}

export interface DebounceControls {
  isPending: () => boolean
  flush: () => void
  cancel: () => void
}

type TimerRef = { current: NodeJS.Timeout | null }

function resetTimer<A>(timeoutRef: TimerRef, lastArgsRef: { current: A | null }) {
  if (timeoutRef.current) clearTimeout(timeoutRef.current)
  timeoutRef.current = null
  lastArgsRef.current = null
}

export function useDebouncedCallback<T extends (...args: unknown[]) => unknown>(
  callback: T,
  delay: number
): [(...args: Parameters<T>) => void, () => void, DebounceControls] {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const callbackRef = useRef(callback)
  const lastArgsRef = useRef<Parameters<T> | null>(null)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => () => resetTimer(timeoutRef, lastArgsRef), [])

  const debouncedFn = useCallback(
    (...args: Parameters<T>) => {
      resetTimer(timeoutRef, lastArgsRef)
      lastArgsRef.current = args
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args)
        timeoutRef.current = null
        lastArgsRef.current = null
      }, delay)
    },
    [delay]
  )

  const cancel = useCallback(() => resetTimer(timeoutRef, lastArgsRef), [])

  const controls: DebounceControls = {
    isPending: () => timeoutRef.current !== null,
    flush: () => {
      if (!timeoutRef.current || !lastArgsRef.current) return
      clearTimeout(timeoutRef.current)
      callbackRef.current(...lastArgsRef.current)
      timeoutRef.current = null
      lastArgsRef.current = null
    },
    cancel,
  }

  return [debouncedFn, cancel, controls]
}
